/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import {
    string, arrayOf, number, shape,
} from 'prop-types';
import isRetailOrSale from '../../../../../helpers/isRetailOrSale';

// helper function matches (") and replaces with (\")
const mdParser = (answer) => answer?.replace(/"/gi, '\\"');

// temporary helper function
const determineBrandDomain = (brand) => {
    if (brand.code === 'CAN') {
        return '1800flowers.ca';
    }
    return `${brand.domain}.com`;
};

const determineImageUrl = (brand) => {
    // Canada is working with the same as 18F.
    // That's why brand.domain and brand.identifier don't work for .CA. It will lead to an invalid image url.
    if (brand.code === 'CAN') {
        return '1800flowers.com/wcsstore/Flowers';
    }
    return `${brand.domain}.com/wcsstore/${brand.identifier}`;
};

const determineBrand = (brand) => {
    if (brand.code === 'PLA') {
        return `${brand.identifier}.com`;
    }
    return brand.identifier;
};

const GraphqlProductSeoSchema = ({
    name, shortDescription, partNumber, productSkus, location, brand, reviews, currency,
}) => (
    <>
        <Helmet>
            <script type="application/ld+json">
                {`{"@context":"https://schema.org/","@type":"Product",${reviews?.average_score && reviews?.total_reviews
                    ? `"aggregateRating":{"@type":"AggregateRating","ratingValue":"${reviews.average_score}","reviewCount":"${reviews.total_reviews}"},`
                    : ''
                }"name":"${mdParser(name)}","image":["https://cdn1.${determineImageUrl(brand)}/images/catalog/${productSkus[0].image.name
                }x.jpg"],"description":"${mdParser(
                    shortDescription)
                }","sku":"${partNumber}","brand":{"@type":"Brand","name":"${determineBrand(brand)
                }"},"offers":[${productSkus?.map(
                    (d) => (`{"@type":"Offer","url":"https://www.${determineBrandDomain(brand)}${location.pathname
                        }","priceCurrency":"${currency || 'USD'}","price":"${isRetailOrSale(
                            d.prices,
                        )}","priceValidUntil":"2024-02-28","sku":"${d.id
                        }","itemCondition":"https://schema.org/NewCondition","availability":"https://schema.org/InStock","seller":{"@type":"Organization","name":"${determineBrand(brand)
                        }"}}`
                    ))}]}`}
            </script>
        </Helmet>
    </>
);

GraphqlProductSeoSchema.propTypes = {
    name: string.isRequired,
    shortDescription: string.isRequired,
    partNumber: string.isRequired,
    productSkus: arrayOf(
        shape({
            id: string.isRequired,
            prices: arrayOf(
                shape({
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    location: shape({
        pathname: string.isRequired,
    }).isRequired,
    brand: shape({
        domain: 'string',
        identifier: 'string',
        description: 'string',
    }).isRequired,
    reviews: shape({
        average_score: 'string',
        total_reviews: 'string',
    }).isRequired,
    currency: string,
};

GraphqlProductSeoSchema.defaultProps = {
    currency: 'USD',
};

export default GraphqlProductSeoSchema;
